import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardBody, Checkbox, Flex, FormControl, FormLabel, Heading, HStack, Input, Switch, Text,
  Tag, TagCloseButton, TagLabel, useToast, VStack,
} from '@chakra-ui/react';
import { TenantSettingsAPI } from '../services/api';

// Post-call owner notifications (scenario gap G3, 2026-10-26): who is emailed when a call creates a
// callback request, a voicemail or a support ticket. Saved into tenant_settings.owner_notifications.
const EVENTS = ['callback', 'voicemail', 'support_ticket'];
const COPY = {
  zh: {
    title: '通话后通知', subtitle: '来电产生回电请求、语音留言或客服工单时，给团队发邮件。',
    enabled: '开启通知', emails: '收件邮箱（最多 5 个；留空则用网页客服的通知邮箱，再退回账户负责人）',
    add: '添加', events: '通知哪些事项', urgentOnly: '只通知紧急的', save: '保存通知设置', saved: '通知设置已保存',
    failed: '保存失败', invalid: '邮箱格式不对', callback: '回电请求', voicemail: '语音留言', support_ticket: '客服工单',
    privacy: '邮件正文会包含来电者的电话（便于回拨）；主题只含工单编号。',
  },
  en: {
    title: 'Post-call notifications', subtitle: 'Email the team when a call creates a callback request, a voicemail or a support ticket.',
    enabled: 'Enabled', emails: 'Recipients (up to 5; empty = the chat widget’s alert emails, then the account owners)',
    add: 'Add', events: 'Events', urgentOnly: 'Urgent only', save: 'Save notifications', saved: 'Notification settings saved',
    failed: 'Save failed', invalid: 'That is not a valid email', callback: 'Callback requests', voicemail: 'Voicemails',
    support_ticket: 'Support tickets',
    privacy: 'The email body includes the caller’s number so staff can call back; the subject carries only the reference.',
  },
};
const EMAIL_RE = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;

export default function OwnerNotificationsCard({ tenantId, lang = 'en', initial }) {
  const copy = COPY[lang === 'zh' ? 'zh' : 'en'];
  const toast = useToast();
  const [enabled, setEnabled] = useState(true);
  const [emails, setEmails] = useState([]);
  const [events, setEvents] = useState(EVENTS);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initial) return;
    setEnabled(initial.enabled !== false);
    setEmails(Array.isArray(initial.emails) ? initial.emails : []);
    setEvents(Array.isArray(initial.events) && initial.events.length ? initial.events : EVENTS);
    setUrgentOnly(Boolean(initial.urgent_only));
  }, [initial]);

  const addEmail = () => {
    const value = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast({ title: copy.invalid, status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    if (!emails.includes(value) && emails.length < 5) setEmails([...emails, value]);
    setDraft('');
  };

  const save = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      await TenantSettingsAPI.update(tenantId, {
        owner_notifications: { enabled, emails, events, urgent_only: urgentOnly },
      });
      toast({ title: copy.saved, status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: copy.failed, description: error?.response?.data?.detail?.toString?.() || '', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card mb={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" data-testid="owner-notifications-card">
      <CardBody py={6}>
        <VStack align="stretch" spacing={4}>
          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
            <Box>
              <Heading size="md">{copy.title}</Heading>
              <Text fontSize="sm" color="gray.600">{copy.subtitle}</Text>
            </Box>
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="owner-notifications-enabled" mb="0" fontSize="sm">{copy.enabled}</FormLabel>
              <Switch id="owner-notifications-enabled" isChecked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            </FormControl>
          </Flex>
          <FormControl>
            <FormLabel fontSize="sm">{copy.emails}</FormLabel>
            <HStack flexWrap="wrap" spacing={2} mb={2}>
              {emails.map((email) => (
                <Tag key={email} size="md" borderRadius="full" variant="subtle" colorScheme="blue">
                  <TagLabel>{email}</TagLabel>
                  <TagCloseButton onClick={() => setEmails(emails.filter((e) => e !== email))} />
                </Tag>
              ))}
            </HStack>
            <HStack>
              <Input size="sm" type="email" value={draft} placeholder="ops@example.com" onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }} isDisabled={emails.length >= 5} />
              <Button size="sm" onClick={addEmail} isDisabled={emails.length >= 5}>{copy.add}</Button>
            </HStack>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">{copy.events}</FormLabel>
            <HStack spacing={5} flexWrap="wrap">
              {EVENTS.map((kind) => (
                <Checkbox key={kind} isChecked={events.includes(kind)}
                  onChange={(e) => setEvents(e.target.checked ? [...events, kind] : events.filter((k) => k !== kind))}>
                  {copy[kind]}
                </Checkbox>
              ))}
              <Checkbox isChecked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)}>{copy.urgentOnly}</Checkbox>
            </HStack>
          </FormControl>
          <Text fontSize="xs" color="gray.500">{copy.privacy}</Text>
          <Flex justify="flex-end">
            <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>{copy.save}</Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
}
